import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { DialogActions } from '@mui/material';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete Outlined.svg';
import ArchiveIcon from '@mui/icons-material/Archive';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import GhostButton from '../button/GhostButton';
import PrimaryButton from '../button/PrimaryButton';
import { BodyText, Subheading } from '../typography/PrimaryTypography';

const DeleteConfirmModal = ({
  open,
  delete_label,
  delete_warning,
  allow_delete,
  allow_archive,
  alternate_message_flag,
  alternate_message,
  item_type,
  archive_warning,
  handleCloseDeleteModal,
  handleDelete,
  handleArchive
}: {
  open: boolean;
  delete_label?: string | null;
  delete_warning?: string;
  allow_delete: boolean | null;
  allow_archive: boolean | null;
  alternate_message_flag?: boolean | null;
  alternate_message?: string | null;
  item_type?: string;
  archive_warning?: string;
  handleCloseDeleteModal: () => void;
  handleDelete: (() => void) | null;
  handleArchive?: (() => void) | null;
}) => {
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [archiveLoading, setArchiveLoading] = React.useState(false);
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);

  const handleDeleteClick = async () => {
    setDeleteLoading(true);
    try {
      if (handleDelete) {
        await handleDelete();
      }
      handleCloseDeleteModal();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: DeleteConfirmModal failed on an unknown error while calling handleArchiveClick.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete entity. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setDeleteLoading(false);
    }
    return;
  };
  const handleArchiveClick = async () => {
    setArchiveLoading(true);
    try {
      if (handleArchive) {
        await handleArchive();
      }
      handleCloseDeleteModal();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: DeleteConfirmModal failed on an unknown error while calling handleArchiveClick.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to archive entity. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setArchiveLoading(false);
    }
    return;
  };

  return (
    <Dialog open={open} onClose={handleCloseDeleteModal}>
      {allow_delete ? (
        <Subheading
          weight="Bold"
          sx={{ m: 2 }}
        >{`Are you sure you want to delete ${delete_label}?`}</Subheading>
      ) : (
        <Subheading weight="Bold" sx={{ m: 2 }}>{`Cannot delete ${delete_label}`}</Subheading>
      )}
      <DialogContent>
        {archive_warning && allow_archive && (
          <BodyText id="archive-explainer">{archive_warning}</BodyText>
        )}
        {handleArchive && !allow_archive && (
          <BodyText id="archive-explainer">{delete_label} is already Archived</BodyText>
        )}
        {delete_warning && allow_delete && (
          <>
            <br />
            <BodyText id="delete-explainer" textAlign={'justify'}>
              {alternate_message_flag ? alternate_message : delete_warning}
            </BodyText>
            <br />
          </>
        )}
        {allow_delete === false && (
          <>
            <br />
            <BodyText id="delete-explainer">
              Metric Records found that reference {delete_label}. You must first delete all related
              Metric Records before deleting this {item_type ?? 'item'}.
            </BodyText>
          </>
        )}
        <DialogActions sx={{ mt: 2 }}>
          <GhostButton dark onClick={handleCloseDeleteModal}>
            Cancel
          </GhostButton>
          {handleArchive && (
            <PrimaryButton
              loading={archiveLoading}
              startIcon={<ArchiveIcon />}
              disabled={deleteLoading || !allow_archive}
              onClick={async () => {
                await handleArchiveClick();
              }}
              palette_color="info"
              autoFocus
            >
              Archive
            </PrimaryButton>
          )}
          <PrimaryButton
            startIcon={
              <DeleteIcon height={25} fill="currentColor" stroke="currentColor" strokeWidth={2} />
            }
            disabled={!allow_delete || archiveLoading}
            loading={deleteLoading || allow_delete === null}
            onClick={async () => {
              await handleDeleteClick();
            }}
            palette_color="error"
          >
            Delete
          </PrimaryButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmModal;
